import React from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Also its name for screen readers, e.g. "Search dashboards…". */
  placeholder: string;
  autoFocus?: boolean;
  /**
   * `small` is the height of the toolbar buttons beside it — Download,
   * Columns, Expand — for a box in a panel header, so the row reads as one.
   */
  size?: 'small' | 'medium';
}

/**
 * A box for narrowing a list by typing: a search icon, the text, and a clear
 * button once there is something to clear. Esc clears it too — without
 * closing the window it sits in.
 *
 * Fills the width it's given; set the width on its container. Given none, it
 * is wide enough to show its whole placeholder. For choosing
 * one value from a list, use `SelectMenu` with `searchable` instead.
 */
export function SearchField({ value, onChange, placeholder, autoFocus, size = 'medium' }: SearchFieldProps) {
  return (
    <TextField
      size="small"
      fullWidth
      sx={size === 'small' ? (t) => ({
        '& .MuiInputBase-root': { height: t.ds.sizes.control.sm, ...t.typography.body2 },
      }) : undefined}
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && value) {
          e.stopPropagation();
          onChange('');
        }
      }}
      slotProps={{
        htmlInput: {
          'aria-label': placeholder.replace(/…$/, ''),
          // Where the box sizes itself (a panel header), it is as wide as its
          // own hint, so the hint is never cut. In a wider container it still fills.
          size: placeholder.length + 2,
          // A search is not a form field: no saved entries offered under it.
          autoComplete: 'off',
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" sx={(t) => ({ color: t.tokens.text.tertiary })} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" aria-label="Clear search" onClick={() => onChange('')}>
                <CloseRounded fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
}
